import React, { useMemo, memo, useCallback, useState, useEffect } from "react";
import OrgLogo from "../../Assets/Main/Utils/org-logo.png";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react"; // swap for your existing chevron icon if you have one
import "./sidebar.css";

const HIDDEN_ITEMS = new Set(["cab Settings", "create coupons"]);

// ---- Group config -------------------------------------------------
// Add/adjust groups here.
// `label`    = static text shown on the header row.
// `iconFrom` = which item's icon to borrow for the header (usually the
//              first/primary item in the group).
// `children` = ALL items nested under the dropdown, including the one
//              whose icon you borrowed for the header. The header itself
//              is toggle-only — it does not navigate anywhere.
const GROUPS = [
  {
    key: "rental_group",
    label: "Rental",
    iconFrom: "rental",
    children: ["rental", "rental_property", "rental_booking"],
  },
  {
    key: "team",
    label: "Team/Admin/Agent",
    iconFrom: "team",
    children: ["manage_admins", "team", "agent"],
  },
  {
    key: "home_visit",
    label: "Site Visit/Home Visit",
    iconFrom: "home_visit",
    children: ["home_visit", "site_visit"],
  },
  {
    key: "manage_users",
    label: "Users/Partners",
    iconFrom: "manage_users",
    children: ["manage_users", "partner_roles"],
  },
  {
    key: "event",
    label: "Event/Meeting/News",
    iconFrom: "event",
    children: ["event", "meeting", "news_and_articles"],
  },
  {
    key: "builders",
    label: "Builders",
    iconFrom: "builders",
    children: ["builders", "builder_resale"],
  },
  {
    key: "manage_projects",
    label: "Fresh/Resale Projects",
    iconFrom: "manage_projects",
    children: ["manage_projects", "resale_project"],
  },
  {
    key: "properties_group",
    label: "Fresh/Resale Properties",
    iconFrom: "manage_properties",
    children: ["manage_properties", "properties_types"],
  },
  {
    key: "deals_group",
    label: "Coupons/Rewards",
    iconFrom: "deals",
    children: ["deals", "rewards"],
  },
  {
    key: "bookings",
    label: "Bookings/Payment",
    iconFrom: "bookings",
    children: ["bookings", "payments"],
  },
  {
    key: "invites",
    label: "Invites/Enquiry",
    iconFrom: "invites",
    children: ["invites", "enquiry"],
  },
  {
    key: "vr_history",
    label: "VR Request/History",
    iconFrom: "vr_history",
    children: ["vr_shoot_request", "vr_history"],
  },
];

// Lookup: name_with_underscore -> group config
const CHILD_TO_GROUP = GROUPS.reduce((acc, group) => {
  group.children.forEach((childKey) => {
    acc[childKey] = group;
  });
  return acc;
}, {});
// ---------------------------------------------------------------------

const formatName = (item) => {
  if (item.name === "deals") return "Deals & Offers";
  return item.name.charAt(0).toUpperCase() + item.name.slice(1);
};

const getPath = (item) =>
  item.name === "deals" ? "/dealsAndOffers" : `/${item.name_with_underscore}`;

const SideBarItem = memo(({ item, isActive, onClick, indent }) => {
  const displayName = formatName(item);

  return (
    <div
      onClick={onClick}
      className={`sidebar-menu-item ${isActive ? "active" : ""} ${indent ? "sidebar-menu-item--child" : ""}`}
      style={
        indent
          ? {
            paddingLeft: "44px",
            position: "relative",
          }
          : undefined
      }
    >
      {indent && (
        <span
          style={{
            position: "absolute",
            left: "26px",
            top: "0",
            bottom: "0",
            width: "1px",
            background: "rgba(255,255,255,0.15)",
          }}
        />
      )}
      {item.image && (
        <img
          style={{ width: indent ? "18px" : "22px", height: indent ? "18px" : "22px", flexShrink: 0 }}
          src={item.image}
          alt={item.name || ""}
        />
      )}
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
        title={displayName}
      >
        {displayName}
      </div>
    </div>
  );
});

const SideBarGroup = memo(({ group, childItems, activePath, onItemClick, forceOpen }) => {
  const [open, setOpen] = useState(forceOpen);

  // keep the group expanded automatically if one of its children is active
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const hasActiveChild = childItems.some((item) => getPath(item) === activePath);
  const headerIcon = childItems.find((i) => i.name_with_underscore === group.iconFrom)?.image;

  return (
    <div className="sidebar-menu-group">
      <div
        className={`sidebar-menu-item sidebar-menu-item--parent ${hasActiveChild ? "active" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        style={{ display: "flex", alignItems: "center", gap: "12px", overflow: "hidden" }}
      >
        {headerIcon && (
          <img
            style={{ width: "22px", height: "22px", flexShrink: 0 }}
            src={headerIcon}
            alt={group.label}
          />
        )}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={group.label}
        >
          {group.label}
        </div>
        <ChevronDown
          size={16}
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </div>

      {open && (
        <div
          className="sidebar-menu-children"
          style={{
            background: "rgba(255,255,255,0.03)",
            borderRadius: "12px",
            margin: "4px 0",
            paddingTop: "4px",
            paddingBottom: "4px",
          }}
        >
          {childItems.map((item) => (
            <SideBarItem
              key={item.name_with_underscore}
              item={item}
              indent
              isActive={getPath(item) === activePath}
              onClick={() => onItemClick(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

const SideBar = () => {
  const { admin, payment_executive } = useSelector((state) => state.admin);

  const loggedUser = useMemo(() => {
    return admin?.data ? admin : payment_executive;
  }, [admin, payment_executive]);

  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = useCallback(
    (item) => {
      const role = loggedUser?.data?.role;
      const isDeals = item.name === "deals";

      if (isDeals) {
        navigate("/dealsAndOffers");
      } else if (role === "payment_executive" && item.name_with_underscore === "bookings") {
        navigate(`/bookings/payment_exec/${loggedUser?.data?._id}`);
      } else {
        navigate(`/${item.name_with_underscore}`);
      }
    },
    [navigate, loggedUser]
  );

  // Group the flat access array into render-ready entries:
  // { type: "item", item } OR { type: "group", group, childItems: [...] }
  // The group renders at the position of its FIRST matching child in the
  // backend's access array.
  const renderList = useMemo(() => {
    const access = loggedUser?.data?.access || [];
    const list = [];
    const groupBuckets = {}; // group.key -> { group, childItems: [] }

    access.forEach((item) => {
      if (HIDDEN_ITEMS.has(item.name)) return;

      const group = CHILD_TO_GROUP[item.name_with_underscore];

      if (!group) {
        list.push({ type: "item", item });
        return;
      }

      if (!groupBuckets[group.key]) {
        groupBuckets[group.key] = { group, childItems: [] };
        list.push({ type: "group", key: group.key });
      }
      groupBuckets[group.key].childItems.push(item);
    });

    return list.map((entry) =>
      entry.type === "group" ? { type: "group", ...groupBuckets[entry.key] } : entry
    );
  }, [loggedUser]);

  return (
    <div className="sidebar border-24">
      <div className="sidebar-content">
        <div
          className="sidebar-logo flex-container flex-center"
          style={{ marginRight: "3px" }}
        >
          <Link to="/dashboard">
            <img style={{ width: "160px", height: "62px" }} src={OrgLogo} alt="logo" />
          </Link>
        </div>

        <div className="sidebar-menu">
          {renderList.length ? (
            renderList.map((entry) => {
              if (entry.type === "group") {
                const hasActiveChild = entry.childItems.some(
                  (item) => getPath(item) === location.pathname
                );
                return (
                  <SideBarGroup
                    key={entry.group.key}
                    group={entry.group}
                    childItems={entry.childItems}
                    activePath={location.pathname}
                    onItemClick={handleClick}
                    forceOpen={hasActiveChild}
                  />
                );
              }

              const { item } = entry;
              const isActive = location.pathname === getPath(item);

              return (
                <SideBarItem
                  key={item.name_with_underscore}
                  item={item}
                  isActive={isActive}
                  onClick={() => handleClick(item)}
                />
              );
            })
          ) : (
            <p>No Access</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideBar;